import type { Direction } from '@game/protocol';

/**
 * Estado das teclas de direção, sem nenhuma dependência do Phaser — o que permite
 * testar a lógica de buffer sem navegador.
 *
 * Resolve dois problemas distintos:
 *
 * 1. **Tecla segurada:** a mais recente vence. Ao contornar uma quina o jogador aperta
 *    a segunda direção sem soltar a primeira; uma checagem em ordem fixa ignoraria a
 *    nova tecla e o personagem ficaria raspando na parede.
 *
 * 2. **Toque curto durante a caminhada:** um passo trava o personagem por ~150ms. Um
 *    toque que começa e termina dentro dessa janela não deixa nenhuma tecla segurada
 *    para ser lida depois — sem buffer ele é perdido por completo, e é exatamente isso
 *    que faz um "adadada" rápido parecer que o jogo ignora metade dos comandos.
 */
export class DirectionState {
  /** Teclas seguradas, em ordem de acionamento. O topo é a direção atual. */
  private readonly held: Direction[] = [];

  private buffered: Direction | null = null;
  private bufferedAt = 0;

  press(direction: Direction, nowMs: number): void {
    // O navegador repete `keydown` enquanto a tecla fica pressionada; só a primeira
    // borda interessa, senão o carimbo do buffer seria reiniciado a cada repetição.
    if (this.held.includes(direction)) return;

    this.held.push(direction);
    this.buffered = direction;
    this.bufferedAt = nowMs;
  }

  release(direction: Direction): void {
    const index = this.held.indexOf(direction);
    if (index !== -1) this.held.splice(index, 1);
  }

  /** A aba pode perder o foco com teclas pressionadas; sem isto o `keyup` nunca chega. */
  clear(): void {
    this.held.length = 0;
    this.buffered = null;
  }

  /**
   * Direção a executar agora, consumindo o buffer se for ele que responder.
   *
   * **Só chame quando houver um passo disponível.** Chamar durante o cooldown
   * descartaria o toque bufferizado, que é justamente o que se quer preservar.
   *
   * `windowMs` deve ser a duração de um passo: cobre um toque perdido em qualquer
   * ponto da caminhada, sem deixar um comando velho ecoar depois.
   */
  take(nowMs: number, windowMs: number): Direction | null {
    const held = this.held.at(-1);

    if (held !== undefined) {
      // Com tecla segurada o buffer não tem mais função, e mantê-lo faria um toque
      // antigo disparar um passo extra assim que o jogador soltasse tudo.
      this.buffered = null;
      return held;
    }

    if (this.buffered !== null && nowMs - this.bufferedAt <= windowMs) {
      const direction = this.buffered;
      this.buffered = null;
      return direction;
    }

    return null;
  }
}
